import { useCallback, useMemo, useState } from 'react';
import { useParams } from 'react-router-dom';

import { Box, Stack, Typography } from '@mui/material';

import { useGetSavedRouteAlertsQuery } from 'state/emailAlertsSlices';
import { useGetSavedRoutesQuery } from 'state/savedRouteSlices';

import FontAwesomeIcon from 'features/common/FontAwesomeIcon';
import LoadingSpinner from 'features/spinner/Spinner';
import { dimmed_white_color, secondary_light_text_color } from 'theme/cemTheme';

import { AllAlertsList } from './AllAlertsList';
import { Breadcrumb } from './Breadcrumb';
import { NotificationsPanel } from './NotificationsPanel';
import { RouteMapDisplay } from './alertFields/RouteMapDisplay';
import { filterAlerts, pluralize } from './alertHelpers';
import { NoAlerts, NoResults } from './components/EmptyState';
import { FilterSearchBar } from './components/FilterSearchBar';
import { useNotificationProject } from './useNotificationProject';

import styles from './AssignRoutesSubPage.module.css';

const GAP_SIZE = '0.25rem';

export function AssignRoutesSubPage() {
  const { routeId } = useParams<{ routeId?: string }>();

  const { projectSlug, navigateTo } = useNotificationProject();
  const [searchQuery, setSearchQuery] = useState('');

  const { currentData: savedRouteData, isLoading: isLoadingRoutes } =
    useGetSavedRoutesQuery(projectSlug, {
      skip: !projectSlug,
    });

  const { data: alerts, isLoading: isLoadingAlerts } =
    useGetSavedRouteAlertsQuery(projectSlug!, {
      skip: !projectSlug,
    });

  const route = useMemo(() => {
    if (!savedRouteData || !routeId) return undefined;
    return savedRouteData.savedRoutes.find((r) => r.id === routeId);
  }, [savedRouteData, routeId]);

  const filteredAlerts = useMemo(() => {
    if (!alerts) return [];
    return filterAlerts(alerts, searchQuery);
  }, [alerts, searchQuery]);

  const hasAnyAlerts = (alerts?.length ?? 0) > 0;

  const handleClose = useCallback(() => {
    navigateTo.routes();
  }, [navigateTo]);

  const alertCount = useMemo(() => {
    if (!alerts || !route) return 0;
    return alerts.filter((a) => a.routes.some((r) => r.id === route.id)).length;
  }, [alerts, route]);

  if (!projectSlug || isLoadingRoutes || isLoadingAlerts) {
    return (
      <NotificationsPanel>
        <Stack
          className={styles.contentBlock}
          height="100%"
          alignItems="center"
        >
          <LoadingSpinner large />
        </Stack>
      </NotificationsPanel>
    );
  }

  return (
    <NotificationsPanel childrenWrapperClass={styles.parentWrapper}>
      <Stack gap={GAP_SIZE} height="100%">
        <Stack direction="row" justifyContent="stretch" gap={GAP_SIZE}>
          <Stack width="100%" gap={GAP_SIZE}>
            <Stack className={styles.contentBlock} gap={GAP_SIZE}>
              <Stack direction="row" gap={GAP_SIZE} alignItems="center">
                <Typography
                  variant="h3"
                  align="center"
                  color={dimmed_white_color}
                  padding="0.375rem 0"
                >
                  Route Alert
                </Typography>
                <FontAwesomeIcon
                  name="chevron-right"
                  sx={{ color: secondary_light_text_color }}
                />
                <Breadcrumb handleClose={handleClose} name={route.routeName} />
              </Stack>
              <Typography variant="caption" color={dimmed_white_color}>
                You have {alertCount} {pluralize(alertCount, 'alert')} linked to
                this route.
              </Typography>
            </Stack>
            <Stack className={styles.contentBlock}>
              <FilterSearchBar
                searchTerm={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                placeholder="Search by alert details, route name, or recipient email"
                hasFrame={false}
              />
            </Stack>
          </Stack>
          <Box width="50%">
            <RouteMapDisplay selectedRoutes={[route.id]} />
          </Box>
        </Stack>
        <Stack className={styles.contentBlock} style={{ minHeight: 0 }}>
          {filteredAlerts.length === 0 ? (
            hasAnyAlerts ? (
              <NoResults resourceName="alerts" />
            ) : (
              <NoAlerts />
            )
          ) : (
            <AllAlertsList alerts={filteredAlerts} route={route} />
          )}
        </Stack>
      </Stack>
    </NotificationsPanel>
  );
}
